"use client";

import { CommonCMS } from "@lib/constants/contentful";
import ClickAndCollect from "@lib/constants/contentful/clickAndCollect";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { hasWindow } from "@lib/utils/hasWindow";
import type { Dispatch, SetStateAction, JSX } from "react";
import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import type { FilterValue } from "./filterValues";
import { filterValueStoreType } from "./filterValues";
import type { ShippingMethodTypes } from "@features/checkout/types";
import { useShowPartnerStores } from "./hooks/useShowPartnerStores";
import type { DayOfWeekLabel, StoreData, StoreDataIndex } from "@features/stores/types";

export type StoreLocatorLocation =
    | "storeLocator"
    | "checkout.clickAndCollect"
    | "checkout.parcelShop"
    | "pdp.clickAndCollect"
    | "cartSummary";
export enum MapState {
    Unmounted,
    Mounted,
    Ready,
    Completed,
}

type ClientLocation = {
    lat: number;
    lng: number;
};

type SearchResultLocationBase<TType extends string, TObject> = {
    type: TType;
    // Coordinates can be null from Google so we need to account for that
    coordinates?: google.maps.LatLng | null;
    object: TObject;
};

export type SearchResultGeocodeLocation = SearchResultLocationBase<
    "geocode",
    google.maps.GeocoderResult
>;
export type SearchResultPlaceLocation = SearchResultLocationBase<"place", google.maps.places.Place>;

export type SearchResultLocation = SearchResultGeocodeLocation | SearchResultPlaceLocation;

export type StoreLocatorSearchValidation = {
    isRestricted: boolean;
    isValidating: boolean;
    message: string;
};

export type StoreLocatorSearchControl = {
    searchResult: SearchResultLocation | undefined;
    setSearchResult: Dispatch<SetStateAction<SearchResultLocation | undefined>>;
    searchValidation: StoreLocatorSearchValidation;
};

const unrestrictedSearch: StoreLocatorSearchValidation = {
    isRestricted: false,
    isValidating: false,
    message: "",
};

type ContextProps = {
    selectedStoreId?: string;
    setSelectedStoreId: Dispatch<SetStateAction<string | undefined>>;
    temporarySelectedStoreId?: string;
    setTemporarySelectedStoreId: Dispatch<SetStateAction<string | undefined>>;
    highlightSelectedStore: boolean;
    setHighlightSelectedStore: Dispatch<SetStateAction<boolean>>;
    searchResult?: SearchResultLocation;
    setSearchResult: Dispatch<SetStateAction<SearchResultLocation | undefined>>;
    lastSelect: number;
    setLastSelect: Dispatch<SetStateAction<number>>;
    clientLocation?: ClientLocation;
    setClientLocation: Dispatch<SetStateAction<ClientLocation | undefined>>;
    storeData: StoreData[];
    setStoreData: Dispatch<SetStateAction<any>>;
    storeDataIndex: StoreDataIndex;
    storesWithAvailability?: StoreData[];
    selectedStore: () => StoreData | undefined;
    visibleStores: string[];
    setVisibleStores: Dispatch<SetStateAction<string[]>>;
    dayOfWeekLabels: DayOfWeekLabel;
    mapBounds?: google.maps.LatLngBounds;
    setMapBounds: Dispatch<SetStateAction<google.maps.LatLngBounds | undefined>>;
    isClickAndCollect: boolean;
    setIsClickAndCollect: Dispatch<SetStateAction<boolean>>;
    showPartnerStores: boolean;
    setShowPartnerStores: Dispatch<SetStateAction<boolean>>;
    setIsLoading: Dispatch<SetStateAction<boolean>>;
    isLoading: boolean;
    getShouldShowStore?: (id: string) => boolean;
    mapState: MapState;
    setMapState: Dispatch<SetStateAction<MapState>>;
    filterDistance?: FilterValue;
    setFilterDistance: Dispatch<SetStateAction<FilterValue | undefined>>;
    storeTypeFilter?: FilterValue;
    setStoreTypeFilter: Dispatch<SetStateAction<FilterValue>>;
    storesInList?: number;
    setStoresInList: Dispatch<SetStateAction<number>>;
    onStoreSelected: (store: StoreData) => void;
    setCurrentLocation: () => void;
    showStoreBadge: boolean;
    storeLocatorLocation?: StoreLocatorLocation;
    shippingMethodKey?: ShippingMethodTypes;
    searchValidation: StoreLocatorSearchValidation;
};

export const Controller = createContext<ContextProps>({
    selectedStoreId: "",
    setSelectedStoreId: () => {},
    temporarySelectedStoreId: undefined,
    setTemporarySelectedStoreId: () => {},
    highlightSelectedStore: false,
    setHighlightSelectedStore: () => {},
    searchResult: undefined,
    setSearchResult: () => {},
    lastSelect: 0,
    setLastSelect: () => {},
    clientLocation: undefined,
    setClientLocation: () => {},
    storeData: [],
    setStoreData: () => {},
    storeDataIndex: {},
    selectedStore: () => undefined,
    visibleStores: [],
    setVisibleStores: () => {},
    dayOfWeekLabels: {},
    mapBounds: undefined,
    setMapBounds: () => {},
    isClickAndCollect: false,
    setIsClickAndCollect: () => {},
    showPartnerStores: false,
    setShowPartnerStores: () => {},
    setIsLoading: () => {},
    isLoading: false,
    getShouldShowStore: undefined,
    mapState: MapState.Unmounted,
    setMapState: () => {},
    filterDistance: undefined,
    setFilterDistance: () => {},
    storeTypeFilter: filterValueStoreType.values[0],
    setStoreTypeFilter: () => {},
    storesInList: 0,
    setStoresInList: () => {},
    onStoreSelected: () => {},
    setCurrentLocation: () => {},
    showStoreBadge: true,
    storeLocatorLocation: undefined,
    shippingMethodKey: undefined,
    searchValidation: unrestrictedSearch,
});

type StoreLocatorProviderProps = {
    children: JSX.Element;
    initStores?: StoreData[];
    getStoresWithAvailability?: (stores: StoreData[]) => StoreData[] | undefined;
    shippingMethodKey?: ShippingMethodTypes;
    showStoreBadge?: boolean;
    onStoreSelected?: (store: StoreData) => void;
    searchControl?: StoreLocatorSearchControl;
    storeLocatorLocation?: StoreLocatorLocation;
    /** Off where the caller decides when to use the device's position - checkout only reads it
     *  once the shopper ticks Use my location, rather than prompting as the dialog opens. */
    seedsLocationFromDevice?: boolean;
};

export const Provider = ({
    children,
    initStores = [],
    getStoresWithAvailability,
    shippingMethodKey,
    showStoreBadge = true,
    onStoreSelected = () => {},
    searchControl,
    storeLocatorLocation,
    seedsLocationFromDevice = true,
}: StoreLocatorProviderProps) => {
    const { getMultiple } = useMicrocopy();

    const [storeData, setStoreData] = useState<StoreData[]>(initStores);
    const [isClickAndCollect, setIsClickAndCollect] = useState<boolean>(false);
    const [visibleStores, setVisibleStores] = useState<string[]>([]);
    const [selectedStoreId, setSelectedStoreId] = useState<string>();
    const [temporarySelectedStoreId, setTemporarySelectedStoreId] = useState<string>();
    const [highlightSelectedStore, setHighlightSelectedStore] = useState<boolean>(false);
    const [searchResult, setSearchResult] = useState<SearchResultLocation>();
    const [lastSelect, setLastSelect] = useState<number>(0);
    const [clientLocation, setClientLocation] = useState<ClientLocation>();
    const [mapBounds, setMapBounds] = useState<google.maps.LatLngBounds>();
    const [showPartnerStores, setShowPartnerStores] = useShowPartnerStores();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [mapState, setMapState] = useState(MapState.Unmounted);
    const [filterDistance, setFilterDistance] = useState<FilterValue | undefined>(undefined);
    const [storesInList, setStoresInList] = useState<number>(0);
    const [storeTypeFilter, setStoreTypeFilter] = useState<FilterValue>(
        filterValueStoreType.values[0]
    );

    const microcopies = (() => {
        return (
            getMultiple(CommonCMS.page, [
                ClickAndCollect.workingDays.sunday,
                ClickAndCollect.workingDays.monday,
                ClickAndCollect.workingDays.tuesday,
                ClickAndCollect.workingDays.wednesday,
                ClickAndCollect.workingDays.thursday,
                ClickAndCollect.workingDays.friday,
                ClickAndCollect.workingDays.saturday,
            ]) ?? {}
        );
    })();
    const dayOfWeekLabels = {
        sun: microcopies[ClickAndCollect.workingDays.sunday],
        mon: microcopies[ClickAndCollect.workingDays.monday],
        tue: microcopies[ClickAndCollect.workingDays.tuesday],
        wed: microcopies[ClickAndCollect.workingDays.wednesday],
        thu: microcopies[ClickAndCollect.workingDays.thursday],
        fri: microcopies[ClickAndCollect.workingDays.friday],
        sat: microcopies[ClickAndCollect.workingDays.saturday],
    };

    const storesWithAvailability = useMemo(
        () => getStoresWithAvailability?.(storeData),
        [getStoresWithAvailability, storeData]
    );

    const storeDataIndex = useMemo<StoreDataIndex>(
        () =>
            Object.fromEntries(
                (storesWithAvailability ?? storeData).map((store) => [store.storeId, store])
            ),
        [storeData, storesWithAvailability]
    );

    const selectedStore = useCallback(() => {
        const storeId = temporarySelectedStoreId || selectedStoreId;
        return storeId ? storeDataIndex[storeId] : undefined;
    }, [temporarySelectedStoreId, selectedStoreId, storeDataIndex]);

    const setCurrentLocation = () => {
        navigator?.geolocation?.getCurrentPosition?.((position) => {
            setClientLocation({
                lat: position.coords.latitude,
                lng: position.coords.longitude,
            });
        });
    };

    useEffect(() => {
        if (!hasWindow || !seedsLocationFromDevice) {
            return;
        }
        setCurrentLocation();
    }, []);

    const getShouldShowStore = useCallback(
        (id: string) => showPartnerStores || storeDataIndex[id]?.storeTypeId === "ECCO",
        [showPartnerStores, storeDataIndex]
    );

    return (
        <Controller.Provider
            value={{
                mapState,
                setMapState,
                selectedStoreId,
                setSelectedStoreId,
                temporarySelectedStoreId,
                setTemporarySelectedStoreId,
                isClickAndCollect,
                setIsClickAndCollect,
                highlightSelectedStore,
                setHighlightSelectedStore,
                searchResult,
                setSearchResult,
                showPartnerStores,
                setShowPartnerStores,
                lastSelect,
                setLastSelect,
                setIsLoading,
                clientLocation,
                setClientLocation,
                storeData,
                setStoreData,
                storeDataIndex,
                storesWithAvailability,
                selectedStore,
                visibleStores,
                setVisibleStores,
                dayOfWeekLabels,
                mapBounds,
                setMapBounds,
                isLoading,
                getShouldShowStore,
                filterDistance,
                setFilterDistance,
                storeTypeFilter,
                setStoreTypeFilter,
                storesInList,
                setStoresInList,
                onStoreSelected,
                setCurrentLocation,
                showStoreBadge,
                storeLocatorLocation,
                shippingMethodKey,
                searchValidation: unrestrictedSearch,
                ...searchControl,
            }}
        >
            {children}
        </Controller.Provider>
    );
};
